import cl from 'classnames';
import { ClassNamesConfig, SelectComponentsConfig, GroupBase } from 'react-select-5';
import { PublicBaseSelectProps } from 'react-select-5/base';

import { DropdownIndicator, Menu, ClearIndicator, MultiValueRemove } from './components';
import { SortableValuesContainer, SortableMultiValue } from './components/sortable';
import s from './index.module.css';
import { TSelectMeta } from './types';

export const getComponents = function <Option, IsMulti extends boolean, Group extends GroupBase<Option>>({
  components = {},
  isSortable,
}: TSelectMeta & Partial<PublicBaseSelectProps<Option, IsMulti, Group>>): SelectComponentsConfig<
  Option,
  IsMulti,
  Group
> {
  const baseComponents = {
    ...components,
    Menu: components.Menu ?? Menu,
    DropdownIndicator: components.DropdownIndicator ?? DropdownIndicator,
    ClearIndicator: components.ClearIndicator ?? ClearIndicator,
    MultiValueRemove: components.MultiValueRemove ?? MultiValueRemove,
  };

  if (isSortable) {
    return {
      ...baseComponents,
      MultiValue: components.MultiValue ?? SortableMultiValue,
      ValueContainer: components.ValueContainer ?? SortableValuesContainer,
    };
  }

  return baseComponents;
};

export const getClassNames = function <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  isFocused,
  isError,
  classNames = {},
}: TSelectMeta & Partial<PublicBaseSelectProps<Option, IsMulti, Group>>): ClassNamesConfig<Option, IsMulti, Group> {
  return {
    ...classNames,
    input: (state) => cl(s.input, classNames.input?.(state)),
    container: (state) => cl(s.container, classNames.container?.(state)),
    control: (state) =>
      cl(
        s.control,
        {
          [s['control-focus']]: isFocused,
          [s['control-error']]: isError,
        },
        classNames.control?.(state),
      ),
    menu: (state) => cl(s.menu, classNames.menu?.(state)),
    placeholder: (state) => cl(s.placeholder, classNames.placeholder?.(state)),
    indicatorsContainer: (state) => cl(s['indicators--container'], classNames.indicatorsContainer?.(state)),
    indicatorSeparator: (state) => cl(s['indicator--separator'], classNames.indicatorSeparator?.(state)),
    dropdownIndicator: (state) =>
      cl(
        s['dropdown--indicator'],
        {
          [s['dropdown--indicator-open']]: state.selectProps.menuIsOpen,
        },
        classNames.dropdownIndicator?.(state),
      ),
    option: (state) =>
      cl(
        s.option,
        {
          [s['option-focused']]: state.isFocused,
          [s['option-disabled']]: state.isDisabled,
        },
        classNames.option?.(state),
      ),
    multiValue: (state) => cl(s.multiValue, classNames.multiValue?.(state)),
    valueContainer: (state) =>
      cl(
        s.valueContainer,
        {
          [s.isMulti]: state.isMulti,
        },
        classNames.valueContainer?.(state),
      ),
    clearIndicator: (state) =>
      cl(
        s.clearIndicator,
        {
          [s['clearIndicator-focus']]: state.isFocused,
        },
        classNames.clearIndicator?.(state),
      ),
    multiValueLabel: (state) => cl(s.multiValueLabel, classNames.multiValueLabel?.(state)),
    multiValueRemove: (state) => cl(s.multiValueRemove, classNames.multiValueRemove?.(state)),
    noOptionsMessage: (state) => cl(s.noOptionsMessage, classNames.noOptionsMessage?.(state)),
    loadingMessage: (state) => cl(s.loadingMessage, classNames.loadingMessage?.(state)),
    loadingIndicator: (state) => cl(s.loadingIndicator, classNames.loadingIndicator?.(state)),
  };
};
